/**
 * Address Normalization and Parsing Utilities
 *
 * For EVM addresses: Normalize to ethereum: prefix for cross-chain sharing
 * - Comments, badges, mentions for polygon:0xAlice are stored as ethereum:0xAlice
 * - All EVM chains (ethereum, polygon, bsc, etc.) share the same data
 * - Original chain context preserved in targetChain and targetCompositeId fields
 *
 * For non-EVM addresses: Keep chain-specific (bitcoin:1ABC, litecoin:LXYZ)
 * - Each chain maintains separate data
 *
 * Mention Parsing: Supports multiple address formats
 * - EVM (0x...), Bitcoin, Bitcoin Cash, Litecoin addresses
 */

import { getChain } from './chainUtils';

/**
 * Check if an identifier is an email user
 * @param {string} identifier - User identifier to check
 * @returns {boolean}
 */
export function isEmailUserIdentifier(identifier) {
  if (!identifier) return false;
  return identifier.startsWith('e_');
}

/**
 * Regex pattern for matching cryptocurrency addresses in mentions
 * Supports: EVM, Bitcoin (legacy, SegWit), Bitcoin Cash (CashAddr), Litecoin (legacy, SegWit)
 * Note: Matches plain addresses without @ prefix
 */
export const ADDRESS_MENTION_REGEX = /\b(0x[a-fA-F0-9]{40}|[13][a-km-zA-HJ-NP-Z1-9]{25,34}|bc1[a-z0-9]{39,59}|[qpzry9x8gf2tvdw0s3jn54khce6mua7l]{42}|[LM][a-km-zA-HJ-NP-Z1-9]{25,34}|ltc1[a-z0-9]{39,59})\b/g;

export function isEVMAddress(address) {
  return address.match(/^0x[a-fA-F0-9]{40}$/);
}

/**
 * Normalize EVM addresses to ethereum: prefix for cross-chain sharing
 * Non-EVM addresses remain chain-specific
 *
 * Used for database storage keys to enable cross-chain data sharing
 *
 * @param {string} chain - Chain name (ethereum, polygon, bitcoin, etc.)
 * @param {string} address - Address string
 * @returns {string} Normalized composite ID (e.g., 'ethereum:0xabc...')
 *
 * @example
 * normalizeAddressKey('polygon', '0xABC...') → 'ethereum:0xabc...'
 * normalizeAddressKey('ethereum', '0xABC...') → 'ethereum:0xabc...'
 * normalizeAddressKey('bitcoin', '1ABC...') → 'bitcoin:1ABC...'
 */
export function normalizeAddressKey(chain, address) {
  const chainConfig = getChain(chain);

  // EVM addresses: normalize to ethereum: prefix (all EVM chains share data)
  if (chainConfig?.isEVM && isEVMAddress(address)) {
    return `ethereum:${address.toLowerCase()}`;
  }

  // Non-EVM: keep chain-specific
  return `${chain}:${address}`;
}

/**
 * Get normalized address without chain prefix
 * Lowercases EVM addresses, keeps others as-is
 *
 * Used for targetAddress field in database (for queries)
 *
 * @param {string} address - Address string
 * @returns {string} Normalized address (lowercase for all)
 *
 * @example
 * normalizeAddress('0xABC...') → '0xabc...'
 * normalizeAddress('1ABC...') → '1ABC...'
 */
// | Chain Type                   | Encoding | Case Handling    | Example          |
// |------------------------------|----------|------------------|------------------|
// | EVM (Ethereum, Polygon, BSC) | Hex      | Case-insensitive | 0xabc = 0xABC    |
// | Bitcoin/Litecoin Legacy      | Base58   | Case-sensitive   | 1ABC ≠ 1abc      |
// | Bitcoin/Litecoin SegWit      | Bech32   | Lowercase-only   | bc1q...          |
// | Bitcoin Cash                 | Bech32   | Lowercase-only   | bitcoincash:q... |
export function normalizeAddress(address, chain = null) {
  // Remove all whitespace
  const afterRemovingWhitespaces = address.trim().replace(/\s+/g, '');
  if (!afterRemovingWhitespaces) return '';

  if (chain === 'bitcoincash' || isEVMAddress(afterRemovingWhitespaces)) {
    return afterRemovingWhitespaces.toLowerCase();
  }

  return afterRemovingWhitespaces;
}

/**
 * Create composite ID (chain-agnostic for all addresses)
 *
 * Used for targetCompositeId, authorCompositeId, and document IDs
 * Format: Just the address (no chain prefix)
 * - EVM addresses: lowercase hex (e.g., '0xabc...')
 * - Bitcoin: address as-is (e.g., '1A1zP1eP...')
 * - Other chains: address as-is
 *
 * @param {string} chain - Original chain name (unused, kept for backwards compatibility)
 * @param {string} address - Address string
 * @returns {string} Composite ID (just the address)
 *
 * @example
 * createCompositeId('polygon', '0xABC...') → '0xabc...'
 * createCompositeId('ethereum', '0xABC...') → '0xabc...'
 * createCompositeId('bitcoin', '1A1zP1eP...') → '1A1zP1eP...'
 */
export function createCompositeId(chain, address) {
  // All addresses: just return the normalized address (no chain prefix)
  return normalizeAddress(address);
}

/**
 * Parse composite ID back to chain and address
 * Handles both new format (just address) and legacy format (chain_address)
 *
 * @param {string} compositeId - Composite ID
 * @returns {{chain: string, address: string}} Chain and address
 *
 * @example
 * parseCompositeId('0xabc...') → { chain: 'ethereum', address: '0xabc...' }
 * parseCompositeId('1A1zP1...') → { chain: 'bitcoin', address: '1A1zP1...' }
 * parseCompositeId('polygon_0xabc...') → { chain: 'polygon', address: '0xabc...' } // legacy
 */
export function parseCompositeId(compositeId) {
  const firstUnderscore = compositeId.indexOf('_');

  // New format: just the address (no underscore)
  if (firstUnderscore === -1) {
    // Detect chain from address format
    if (isEVMAddress(compositeId)) {
      return { chain: 'ethereum', address: compositeId };
    }
    // Bitcoin addresses start with 1, 3, or bc1
    if (compositeId.match(/^(1|3|bc1)/)) {
      return { chain: 'bitcoin', address: compositeId };
    }
    // Litecoin addresses start with L, M, or ltc1
    if (compositeId.match(/^(L|M|ltc1)/)) {
      return { chain: 'litecoin', address: compositeId };
    }
    // Bitcoin Cash
    if (compositeId.startsWith('bitcoincash:')) {
      return { chain: 'bitcoincash', address: compositeId };
    }
    // Default to ethereum for unknown format
    return { chain: 'ethereum', address: compositeId };
  }

  // Legacy format: chain_address
  const chain = compositeId.substring(0, firstUnderscore);
  const address = compositeId.substring(firstUnderscore + 1);

  return { chain, address };
}

/**
 * Check if an address should use normalized storage
 * (i.e., if it's an EVM address that shares data across chains)
 *
 * @param {string} chain - Chain name
 * @param {string} address - Address string
 * @returns {boolean} True if address uses normalized storage
 *
 * @example
 * usesNormalizedStorage('polygon', '0xABC...') → true
 * usesNormalizedStorage('bitcoin', '1ABC...') → false
 */
export function usesNormalizedStorage(chain, address) {
  const chainConfig = getChain(chain);
  return chainConfig?.isEVM && address.match(/^0x[a-fA-F0-9]{40}$/);
}

/**
 * Get all chain variants for an address (for display purposes)
 * Returns array of {chain, compositeId} for all chains where this address exists
 *
 * For EVM addresses: Returns all enabled EVM chains
 * For non-EVM: Returns only the specific chain
 *
 * @param {string} chain - Original chain
 * @param {string} address - Address string
 * @returns {Array<{chain: string, compositeId: string}>} All chain variants
 *
 * @example
 * getChainVariants('polygon', '0xABC...')
 * → [
 *     { chain: 'ethereum', compositeId: 'ethereum_0xabc...' },
 *     { chain: 'polygon', compositeId: 'polygon_0xabc...' },
 *     { chain: 'bsc', compositeId: 'bsc_0xabc...' },
 *     ...
 *   ]
 *
 * getChainVariants('bitcoin', '1ABC...')
 * → [{ chain: 'bitcoin', compositeId: 'bitcoin_1ABC...' }]
 */
export function getChainVariants(chain, address) {
  const chainConfig = getChain(chain);

  // EVM: return all enabled EVM chains
  if (chainConfig?.isEVM && address.match(/^0x[a-fA-F0-9]{40}$/)) {
    const { getEnabledEVMChains } = require('./chainUtils');
    const evmChains = getEnabledEVMChains();

    return evmChains.map(chainId => ({
      chain: chainId,
      compositeId: createCompositeId(chainId, address)
    }));
  }

  // Non-EVM: return only the specific chain
  return [{
    chain,
    compositeId: createCompositeId(chain, address)
  }];
}

/**
 * Format an address for display (shortened version)
 * @param {string} address - The address to format
 * @param {string} chain - Optional chain for chain-specific formatting
 * @returns {string} Formatted address (e.g., "0x1234...5678")
 */
export function formatAddress(address, chain = null) {
  if (!address) return '';

  // If no chain specified or chain is EVM, use default formatting
  if (!chain || isEVMAddress(address)) {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }

  // Chain-specific formatting for non-EVM addresses
  // Remove Bitcoin Cash prefix for display if present
  let displayAddress = address;
  if (chain === 'bitcoincash' && address.startsWith('bitcoincash:')) {
    displayAddress = address.substring(12);
  }

  // Different truncation strategies for different chains
  switch (chain) {
    case 'bitcoin':
      // SegWit addresses (bc1...) are longer, show more
      if (displayAddress.startsWith('bc1')) {
        return `${displayAddress.slice(0, 10)}...${displayAddress.slice(-6)}`;
      }
      // Legacy addresses (1... or 3...)
      return `${displayAddress.slice(0, 8)}...${displayAddress.slice(-6)}`;

    case 'bitcoincash':
    case 'litecoin':
      // Similar to Bitcoin
      if (displayAddress.startsWith('ltc1') || displayAddress.startsWith('bc1')) {
        return `${displayAddress.slice(0, 10)}...${displayAddress.slice(-6)}`;
      }
      return `${displayAddress.slice(0, 8)}...${displayAddress.slice(-6)}`;

    default:
      // Default: show first 6, last 4
      return `${displayAddress.slice(0, 6)}...${displayAddress.slice(-4)}`;
  }
}
